import {Alert, Avatar, Box, Button, Card,
    CardActions, CardContent, Container, Divider, Stack, TextField, Typography } from "@mui/material";
import React, {useEffect, useState} from "react";
import {PageHeader} from "../components/layouts/PageHeader";
import {SettingsCard} from "../components/layouts/SettingsCard";
import {useUpdateUser, useUser} from '../data/user';
import {useLocation} from 'react-router-dom';
import {EmailNotificationsCard} from '../components/account/EmailNotificationsCard';
import {DeleteAccountCard} from '../components/account/DeleteAccountCard';
import {confirmationMessage} from '../utilities/emailNotifications';
import {signInSummary} from '../utilities/sso';

export function ProfilePage(){
    const [isEditing, setIsEditing] = useState(false);
    const [email, setEmail] = useState('');
    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');

    const {data: user, isLoading} = useUser();
    // Opened from the link in the confirmation email: say how it went.
    const confirmed = confirmationMessage(useLocation().search);

    useEffect(() => {
        if (user?.email) setEmail(user.email);
        if (user?.firstName) setFirstName(user.firstName);
        if (user?.lastName) setLastName(user.lastName);

    }, [user?.email, user?.firstName, user?.lastName]);

    const {mutate: updateUser} = useUpdateUser();

    function handleSubmit(isEditMode: boolean) {
        setIsEditing(!isEditMode);
        if (isEditMode) {
            updateUser({email, firstName, lastName});
        }
    }

    const displayName = [firstName, lastName].filter(Boolean).join(' ') || email;

    return (
        <Box sx={{maxWidth: 640, mx: 'auto'}}>
            <PageHeader title="Account" subtitle="Your profile details" />

            {confirmed && <Alert severity={confirmed.severity} sx={{mb: 2}}>{confirmed.text}</Alert>}

            {!isLoading && !user && (
                <Alert severity="error">You don't have access to account settings.</Alert>
            )}

            {!isLoading && user && (
                <SettingsCard
                    title="Profile"
                    action={
                        <Button onClick={() => handleSubmit(isEditing)} variant={isEditing ? 'contained' : 'outlined'}>
                            {isEditing ? 'Save' : 'Edit'}
                        </Button>
                    }
                >
                    <Stack direction="row" spacing={2} alignItems="center" sx={{mb: 3}}>
                        <Avatar sx={{width: 56, height: 56, bgcolor: 'primary.main'}}>
                            {(firstName || email).charAt(0).toUpperCase()}
                        </Avatar>
                        <Box sx={{minWidth: 0}}>
                            <Typography variant="h6" noWrap>{displayName}</Typography>
                            <Typography variant="body2" color="text.secondary" noWrap>{user.email}</Typography>
                        </Box>
                    </Stack>

                    <Box sx={{display: 'grid', gap: 2, gridTemplateColumns: {xs: '1fr', sm: '1fr 1fr'}}}>
                        <TextField label="First name" value={firstName} onChange={(e) => setFirstName(e.target.value)} disabled={!isEditing} />
                        <TextField label="Last name" value={lastName} onChange={(e) => setLastName(e.target.value)} disabled={!isEditing} />
                        <TextField label="Email" value={email} disabled sx={{gridColumn: {sm: '1 / -1'}}} />
                    </Box>
                    {signInSummary(user) && (
                        <Typography variant="body2" color="text.secondary" sx={{mt: 2}}>{signInSummary(user)}</Typography>
                    )}
                </SettingsCard>
            )}

            {!isLoading && user && (
                <Box sx={{mt: 2}}>
                    <EmailNotificationsCard/>
                </Box>
            )}

            {!isLoading && user && (
                <Box sx={{mt: 2}}>
                    <DeleteAccountCard temporary={!!user.temporary} hasPassword={user.hasPassword !== false}/>
                </Box>
            )}
        </Box>
    );
}
